// src/hooks/useKarigar.js
// ============================================================================
// KARIGAR HOOKS - UPDATED VERSION
// ============================================================================
// Changes:
// 1. Added removeJobDeduction thunk and method
// 2. Added cancelPayment thunk and method
// 3. Added new payment selectors (completed, pending, karigarPayment)
// 4. Added new loading state selectors
// 5. Added clearPendingPayments action
// ============================================================================
//
// KEY EXPORTS:
// - useKarigarManagement: Karigar CRUD operations
// - useJobManagement: Job lifecycle, materials (with CRUD), and image management
// - usePaymentManagement: Payment processing and calculations
// - useKarigar: Combined hook for all three

import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

// ============================================================================
// THUNKS IMPORTS
// ============================================================================

// Karigar thunks
import {
  fetchKarigars,
  fetchKarigarById,
  createKarigar,
  updateKarigar,
  updateKarigarStatus,
  deleteKarigar,
  fetchKarigarsByBranch,
  fetchKarigarAvailability,
  fetchBulkKarigarAvailability,
  fetchKarigarStats,
  addKarigarToBranch,
  removeKarigarFromBranch,
  updateKarigarSkills,
  verifyKarigarSkill,
  updateWorkPreferences,
  searchKarigars,
  fetchAvailableKarigars,
  fetchPerformanceReport,
  approveMultipleKarigars,
  suspendMultipleKarigars,
  deleteMultipleKarigars,
  setKarigarPassword,
  resetKarigarPassword,
  checkKarigarPassword,
  verifyBankDetails,
  fetchEstimatedHoursForProduct,
} from "../store/thunks/karigar/karigarThunks";

// Job thunks - UPDATED with removeJobDeduction
import {
  // List & Search
  fetchJobs,
  fetchJobById,
  fetchJobStats,
  fetchJobTypes,

  // Job Creation
  createJob,
  createRepairJob,
  createAlterationJob,
  createInternalJob,
  receiveItemForWork,

  // Job Editing & Status
  updateJob,
  updateJobStatus,
  assignJob,
  reassignJob,
  cancelJob,
  cancelJobs,

  // Image Management
  fetchJobImages,
  uploadReferenceImages,
  deleteReferenceImage,
  uploadCompletedImages,
  deleteCompletedImage,
  uploadItemImages,
  uploadQCImages,
  bulkDeleteImages,
  uploadDesignFile,

  // Material Management - EXISTING
  fetchJobMaterials,
  issueMaterial,
  confirmMaterialReceived,
  recordMaterialReturn,

  // Material Management - CRUD OPERATIONS
  updateIssuedMaterial,
  deleteIssuedMaterial,
  undoMaterialReceived,
  updateMaterialReturn,
  deleteMaterialReturn,

  // Quality Check Workflow
  submitForQualityCheck,
  performQualityCheck,
  completeRevision,
  markJobDelivered,

  // Job Payment
  fetchJobPayments,
  updateJobPricing,
  recordJobPayment,
  addJobDeduction,
  removeJobDeduction, // NEW

  // Pricing Preview
  fetchPricingPreview,

  // Progress
  addJobProgress,

  // Fetch by Relations
  fetchJobsByKarigar,
  fetchJobsByOrder,
  fetchJobTimeline,

  // Order-Karigar Integration
  fetchAvailableKarigarsForJob,
  assignKarigarToOrderItem,
  reassignKarigarForOrderItem,
  bulkAssignKarigars,
  getOrderKarigarStatus,
  getKarigarQueue,
  getOrderKarigarWorkStatus,
  assignKarigarViaOrder,
  checkOrderCanFulfill,
  sendJobMessage,
} from "../store/thunks/karigar/jobThunks";

// Payment thunks - UPDATED with cancelPayment
import {
  fetchPayments,
  fetchPaymentById,
  createPayment,
  updatePayment,
  approvePayment,
  rejectPayment,
  processPayment,
  holdPayment,
  releasePayment,
  cancelPayment, // NEW
  fetchPaymentsByKarigar,
  fetchPaymentsByJob,
  fetchPendingPayments,
  generatePaymentsForPeriod,
  bulkProcessPayments,
  fetchPaymentStats,
  generatePaymentReport,
  fetchKarigarEarnings,
  createAdvancePayment,
  createDeduction,
  calculateJobPayment,
  exportPayments,
} from "../store/thunks/karigar/paymentThunks";

// ============================================================================
// SLICE ACTION IMPORTS
// ============================================================================

// Karigar slice actions
import {
  clearError as clearKarigarError,
  clearSuccessMessage as clearKarigarSuccessMessage,
  setFilters as setKarigarFilters,
  resetFilters as resetKarigarFilters,
  clearCurrentKarigar,
  clearAvailability,
  clearAvailableKarigars as clearKarigarAvailableKarigars,
  clearPerformanceReport,
  clearBulkAvailability,
  clearEstimatedHours,
} from "../store/slices/karigar/karigarSlice";

// Job slice actions
import {
  clearError as clearJobError,
  clearSuccessMessage as clearJobSuccessMessage,
  setFilters as setJobFilters,
  resetFilters as resetJobFilters,
  clearCurrentJob,
  clearAvailableKarigars,
  clearTimeline,
  clearOrderStatus,
  clearKarigarQueue,
  clearJobMaterials,
  clearJobPayments,
  clearCanFulfillCheck,
  clearOrderKarigarWorkStatus,
  clearJobImages,
  setMaterialPermissions,
  clearPricingPreview,
} from "../store/slices/karigar/jobSlice";

// Payment slice actions - UPDATED with new actions
import {
  clearError as clearPaymentError,
  clearSuccessMessage as clearPaymentSuccessMessage,
  setFilters as setPaymentFilters,
  resetFilters as resetPaymentFilters,
  clearCurrentPayment,
  clearReport,
  clearEarnings,
  clearJobPaymentCalculation,
  clearPendingPayments, // NEW
  setPagination as setPaymentPagination,
  updatePaymentInList, // NEW
  removeFromPending, // NEW
} from "../store/slices/karigar/paymentSlice";

// ============================================================================
// SELECTORS IMPORTS
// ============================================================================

import * as selectors from "../store/selectors/karigarSelectors";

// ============================================================================
// HOOK: useKarigarManagement
// ============================================================================

export const useKarigarManagement = () => {
  const dispatch = useDispatch();

  // Selectors
  const karigars = useSelector(selectors.selectKarigars);
  const currentKarigar = useSelector(selectors.selectCurrentKarigar);
  const pagination = useSelector(selectors.selectKarigarPagination);
  const filters = useSelector(selectors.selectKarigarFilters);
  const loading = useSelector(selectors.selectKarigarLoading);
  const error = useSelector(selectors.selectKarigarError);
  const successMessage = useSelector(selectors.selectKarigarSuccessMessage);
  const availability = useSelector(selectors.selectKarigarAvailability);
  const bulkAvailability = useSelector(selectors.selectBulkKarigarAvailability);
  const stats = useSelector(selectors.selectKarigarStats);
  const availableKarigars = useSelector(
    selectors.selectAvailableKarigarsFromKarigarState
  );
  const performanceReport = useSelector(selectors.selectPerformanceReport);
  const passwordStatus = useSelector(selectors.selectPasswordStatus);
  const estimatedHours = useSelector(selectors.selectEstimatedHours);
  const allowedTransitions = useSelector(selectors.selectAllowedTransitions);
  const canBeModified = useSelector(selectors.selectCanBeModified);

  // ========== LIST & SEARCH ==========
  const getKarigars = useCallback(
    (params) => dispatch(fetchKarigars(params)),
    [dispatch]
  );

  const getKarigarById = useCallback(
    (id) => dispatch(fetchKarigarById(id)),
    [dispatch]
  );

  const search = useCallback(
    (params) => dispatch(searchKarigars(params)),
    [dispatch]
  );

  const getAvailableKarigars = useCallback(
    (params) => dispatch(fetchAvailableKarigars(params)),
    [dispatch]
  );

  const getKarigarsByBranch = useCallback(
    (branchCode, params) =>
      dispatch(fetchKarigarsByBranch({ branchCode, params })),
    [dispatch]
  );

  const getStats = useCallback(
    (params) => dispatch(fetchKarigarStats(params)),
    [dispatch]
  );

  // ========== CRUD ==========
  const addKarigar = useCallback(
    (data) => dispatch(createKarigar(data)),
    [dispatch]
  );

  const editKarigar = useCallback(
    (id, data) => dispatch(updateKarigar({ karigarId: id, data })),
    [dispatch]
  );

  const removeKarigar = useCallback(
    (id) => dispatch(deleteKarigar(id)),
    [dispatch]
  );

  // ========== STATUS & AVAILABILITY ==========
  const changeStatus = useCallback(
    (id, status, message) =>
      dispatch(
        updateKarigarStatus({ karigarId: id, status, adminMessage: message })
      ),
    [dispatch]
  );

  const getAvailability = useCallback(
    (id, params) =>
      dispatch(fetchKarigarAvailability({ karigarId: id, params })),
    [dispatch]
  );

  const getBulkAvailability = useCallback(
    (data) => dispatch(fetchBulkKarigarAvailability(data)),
    [dispatch]
  );

  const getEstimatedHours = useCallback(
    (karigarId, params) =>
      dispatch(fetchEstimatedHoursForProduct({ karigarId, params })),
    [dispatch]
  );

  // ========== BRANCH MANAGEMENT ==========
  const addToBranch = useCallback(
    (karigarId, branchData) =>
      dispatch(addKarigarToBranch({ karigarId, branchData })),
    [dispatch]
  );

  const removeFromBranch = useCallback(
    (karigarId, branchCode) =>
      dispatch(removeKarigarFromBranch({ karigarId, branchCode })),
    [dispatch]
  );

  // ========== SKILLS & PREFERENCES ==========
  const updateSkills = useCallback(
    (karigarId, skills) => dispatch(updateKarigarSkills({ karigarId, skills })),
    [dispatch]
  );

  const verifySkill = useCallback(
    (karigarId, skillCategory, verificationDetails) =>
      dispatch(
        verifyKarigarSkill({ karigarId, skillCategory, verificationDetails })
      ),
    [dispatch]
  );

  const updatePreferences = useCallback(
    (karigarId, preferences) =>
      dispatch(updateWorkPreferences({ karigarId, preferences })),
    [dispatch]
  );

  // ========== REPORTS & ANALYTICS ==========
  const getPerformanceReport = useCallback(
    (karigarId, params) =>
      dispatch(fetchPerformanceReport({ karigarId, params })),
    [dispatch]
  );

  // ========== BULK OPERATIONS ==========
  const approveMultiple = useCallback(
    (karigarIds) => dispatch(approveMultipleKarigars(karigarIds)),
    [dispatch]
  );

  const suspendMultiple = useCallback(
    (karigarIds, reason) =>
      dispatch(suspendMultipleKarigars({ karigarIds, reason })),
    [dispatch]
  );

  const deleteMultiple = useCallback(
    (karigarIds) => dispatch(deleteMultipleKarigars(karigarIds)),
    [dispatch]
  );

  // ========== PASSWORD MANAGEMENT ==========
  const setPassword = useCallback(
    (karigarId, password) =>
      dispatch(setKarigarPassword({ karigarId, password })),
    [dispatch]
  );

  const resetPassword = useCallback(
    (karigarId, sendSms) =>
      dispatch(resetKarigarPassword({ karigarId, sendSms })),
    [dispatch]
  );

  const checkPassword = useCallback(
    (karigarId) => dispatch(checkKarigarPassword(karigarId)),
    [dispatch]
  );

  // ========== VERIFICATION ==========
  const verifyBank = useCallback(
    (karigarId, verificationNotes) =>
      dispatch(verifyBankDetails({ karigarId, verificationNotes })),
    [dispatch]
  );

  // ========== MESSAGING ==========
  const sendMessage = useCallback(
    (jobId, message, attachments) =>
      dispatch(sendJobMessage({ jobId, message, attachments })),
    [dispatch]
  );

  // ========== FILTERS & STATE ==========
  const updateFilters = useCallback(
    (newFilters) => dispatch(setKarigarFilters(newFilters)),
    [dispatch]
  );

  const clearFilters = useCallback(
    () => dispatch(resetKarigarFilters()),
    [dispatch]
  );

  const clearKarigar = useCallback(
    () => dispatch(clearCurrentKarigar()),
    [dispatch]
  );

  const clearAvail = useCallback(
    () => dispatch(clearAvailability()),
    [dispatch]
  );

  const clearReport = useCallback(
    () => dispatch(clearPerformanceReport()),
    [dispatch]
  );

  const clearBulkAvail = useCallback(
    () => dispatch(clearBulkAvailability()),
    [dispatch]
  );

  const clearEstHours = useCallback(
    () => dispatch(clearEstimatedHours()),
    [dispatch]
  );

  const clearErr = useCallback(() => dispatch(clearKarigarError()), [dispatch]);

  const clearSuccess = useCallback(
    () => dispatch(clearKarigarSuccessMessage()),
    [dispatch]
  );

  return {
    // State
    karigars,
    currentKarigar,
    pagination,
    filters,
    loading,
    error,
    successMessage,
    availability,
    bulkAvailability,
    stats,
    availableKarigars,
    performanceReport,
    passwordStatus,
    estimatedHours,
    allowedTransitions,
    canBeModified,

    // List & Search
    getKarigars,
    getKarigarById,
    search,
    getAvailableKarigars,
    getKarigarsByBranch,
    getStats,

    // CRUD
    addKarigar,
    editKarigar,
    removeKarigar,

    // Status & Availability
    changeStatus,
    getAvailability,
    getBulkAvailability,
    getEstimatedHours,

    // Branch Management
    addToBranch,
    removeFromBranch,

    // Skills & Preferences
    updateSkills,
    verifySkill,
    updatePreferences,

    // Reports
    getPerformanceReport,

    // Bulk Operations
    approveMultiple,
    suspendMultiple,
    deleteMultiple,

    // Password Management
    setPassword,
    resetPassword,
    checkPassword,

    // Verification
    verifyBank,

    // Messaging
    sendMessage,

    // Filters & State
    updateFilters,
    clearFilters,
    clearCurrentKarigar: clearKarigar,
    clearAvailability: clearAvail,
    clearPerformanceReport: clearReport,
    clearBulkAvailability: clearBulkAvail,
    clearEstimatedHours: clearEstHours,
    clearError: clearErr,
    clearSuccessMessage: clearSuccess,
  };
};

// ============================================================================
// HOOK: useJobManagement
// ============================================================================

export const useJobManagement = () => {
  const dispatch = useDispatch();

  // Selectors - EXISTING
  const jobs = useSelector(selectors.selectJobs);
  const currentJob = useSelector(selectors.selectCurrentJob);
  const orderDetails = useSelector(selectors.selectOrderDetails);
  const productDetails = useSelector(selectors.selectProductDetails);
  const pagination = useSelector(selectors.selectJobPagination);
  const filters = useSelector(selectors.selectJobFilters);
  const loading = useSelector(selectors.selectJobLoading);
  const error = useSelector(selectors.selectJobError);
  const successMessage = useSelector(selectors.selectJobSuccessMessage);
  const availableKarigars = useSelector(selectors.selectAvailableKarigars);
  const productKarigars = useSelector(
    selectors.selectProductKarigarsForAssignment
  );
  const otherKarigars = useSelector(selectors.selectOtherKarigarsForAssignment);
  const allKarigars = useSelector(selectors.selectAllKarigarsForAssignment);
  const recommendations = useSelector(selectors.selectKarigarRecommendations);
  const availableKarigarsSummary = useSelector(
    selectors.selectAvailableKarigarsSummary
  );
  const stats = useSelector(selectors.selectJobStats);
  const timeline = useSelector(selectors.selectJobTimeline);
  const orderStatus = useSelector(selectors.selectOrderKarigarStatus);
  const karigarQueue = useSelector(selectors.selectKarigarQueue);
  const jobTypes = useSelector(selectors.selectJobTypes);
  const currentJobMaterials = useSelector(selectors.selectCurrentJobMaterials);
  const currentJobPayments = useSelector(selectors.selectCurrentJobPayments);
  const currentJobImages = useSelector(selectors.selectCurrentJobImages);
  const orderKarigarWorkStatus = useSelector(
    selectors.selectOrderKarigarWorkStatus
  );
  const canFulfillCheck = useSelector(selectors.selectCanFulfillCheck);
  const allowedTransitions = useSelector(selectors.selectAllowedTransitions);
  const canBeModified = useSelector(selectors.selectCanBeModified);

  // Material permission selectors
  const materialPermissions = useSelector(selectors.selectMaterialPermissions);
  const canIssueMaterial = useSelector(selectors.selectCanIssueMaterial);
  const canRecordReturn = useSelector(selectors.selectCanRecordReturn);
  const canEditMaterials = useSelector(selectors.selectCanEditMaterials);

  // Material detail selectors
  const issuedMaterials = useSelector(selectors.selectIssuedMaterials);
  const returnedMaterials = useSelector(selectors.selectReturnedMaterials);
  const materialReturnRecord = useSelector(
    selectors.selectMaterialReturnRecord
  );
  const materialSummary = useSelector(selectors.selectMaterialSummary);
  const wastage = useSelector(selectors.selectWastage);
  const hasMaterialsReturned = useSelector(
    selectors.selectHasMaterialsReturned
  );
  const receivedMaterialsCount = useSelector(
    selectors.selectReceivedMaterialsCount
  );
  const pendingReceiptMaterials = useSelector(
    selectors.selectPendingReceiptMaterials
  );

  // Material loading selectors
  const isMaterialUpdating = useSelector(selectors.selectIsMaterialUpdating);
  const isMaterialDeleting = useSelector(selectors.selectIsMaterialDeleting);
  const isMaterialReturning = useSelector(selectors.selectIsMaterialReturning);
  const isAnyMaterialLoading = useSelector(
    selectors.selectIsAnyMaterialLoading
  );

  // Pricing Preview selectors
  const pricingPreview = useSelector(selectors.selectPricingPreview);
  const pricingPreviewLoading = useSelector(
    selectors.selectPricingPreviewLoading
  );
  const canCalculatePricing = useSelector(selectors.selectCanCalculatePricing);
  const missingPricingFields = useSelector(
    selectors.selectMissingPricingFields
  );
  const allRateTypePreviews = useSelector(selectors.selectAllRateTypePreviews);

  // NEW: Job Payment selectors
  const jobPricing = useSelector(selectors.selectJobPricing);
  const karigarPaymentInfo = useSelector(selectors.selectKarigarPaymentInfo);
  const completedJobPayments = useSelector(
    selectors.selectCompletedJobPayments
  );
  const pendingJobPayments = useSelector(selectors.selectPendingJobPayments);
  const jobDeductions = useSelector(selectors.selectJobDeductions);
  const karigarTotalPayable = useSelector(selectors.selectKarigarTotalPayable);
  const karigarTotalPaid = useSelector(selectors.selectKarigarTotalPaid);
  const karigarPendingAmount = useSelector(
    selectors.selectKarigarPendingAmount
  );
  const karigarPaymentStatus = useSelector(
    selectors.selectKarigarPaymentStatus
  );
  const isJobFullyPaid = useSelector(selectors.selectIsJobFullyPaid);
  const isJobPartiallyPaid = useSelector(selectors.selectIsJobPartiallyPaid);
  const jobHasPayments = useSelector(selectors.selectJobHasPayments);
  const jobHasPendingPayments = useSelector(
    selectors.selectJobHasPendingPayments
  );
  const jobPaymentSummary = useSelector(selectors.selectJobPaymentSummary);

  // NEW: Payment loading selectors
  const isRecordingPayment = useSelector(selectors.selectIsRecordingPayment);
  const isUpdatingPricing = useSelector(selectors.selectIsUpdatingPricing);
  const isAddingDeduction = useSelector(selectors.selectIsAddingDeduction);
  const isRemovingDeduction = useSelector(selectors.selectIsRemovingDeduction);
  const isFetchingJobPayments = useSelector(
    selectors.selectIsFetchingJobPayments
  );
  const isAnyPaymentOperationLoading = useSelector(
    selectors.selectIsAnyPaymentOperationLoading
  );

  // ========== LIST & SEARCH ==========
  const getJobs = useCallback(
    (params) => dispatch(fetchJobs(params)),
    [dispatch]
  );

  const getJobById = useCallback(
    (id) => dispatch(fetchJobById(id)),
    [dispatch]
  );

  const getStats = useCallback(
    (params) => dispatch(fetchJobStats(params)),
    [dispatch]
  );

  const getJobTypes = useCallback(() => dispatch(fetchJobTypes()), [dispatch]);

  const getJobsByKarigar = useCallback(
    (karigarId, params) => dispatch(fetchJobsByKarigar({ karigarId, params })),
    [dispatch]
  );

  const getJobsByOrder = useCallback(
    (orderId) => dispatch(fetchJobsByOrder(orderId)),
    [dispatch]
  );

  const getTimeline = useCallback(
    (jobId) => dispatch(fetchJobTimeline(jobId)),
    [dispatch]
  );

  // ========== JOB CREATION (with image support) ==========
  const addJob = useCallback(
    (jobData, referenceImages = [], itemImages = []) =>
      dispatch(createJob({ jobData, referenceImages, itemImages })),
    [dispatch]
  );

  const addRepairJob = useCallback(
    (jobData, referenceImages = [], itemImages = []) =>
      dispatch(createRepairJob({ jobData, referenceImages, itemImages })),
    [dispatch]
  );

  const addAlterationJob = useCallback(
    (jobData, referenceImages = [], itemImages = []) =>
      dispatch(createAlterationJob({ jobData, referenceImages, itemImages })),
    [dispatch]
  );

  const addInternalJob = useCallback(
    (jobData, referenceImages = []) =>
      dispatch(createInternalJob({ jobData, referenceImages })),
    [dispatch]
  );

  const receiveItem = useCallback(
    (itemData, itemImages = []) =>
      dispatch(receiveItemForWork({ itemData, itemImages })),
    [dispatch]
  );

  // ========== JOB EDITING & STATUS ==========
  const editJob = useCallback(
    (jobId, data) => dispatch(updateJob({ jobId, data })),
    [dispatch]
  );

  const changeStatus = useCallback(
    (jobId, newStatus, notes, forceTransition = false) =>
      dispatch(updateJobStatus({ jobId, newStatus, notes, forceTransition })),
    [dispatch]
  );

  // ========== ASSIGNMENT ==========
  const assign = useCallback(
    (jobId, karigarId, reason) =>
      dispatch(assignJob({ jobId, karigarId, reason })),
    [dispatch]
  );

  const reassign = useCallback(
    (jobId, newKarigarId, reason) =>
      dispatch(reassignJob({ jobId, newKarigarId, reason })),
    [dispatch]
  );

  const getAvailableKarigars = useCallback(
    (data) => dispatch(fetchAvailableKarigarsForJob(data)),
    [dispatch]
  );

  // ========== CANCEL ==========
  const cancel = useCallback(
    (jobId, reason, materialReturned = false) =>
      dispatch(cancelJob({ jobId, reason, materialReturned })),
    [dispatch]
  );

  const cancelMultiple = useCallback(
    (jobIds, reason) => dispatch(cancelJobs({ jobIds, reason })),
    [dispatch]
  );

  // ========== IMAGE MANAGEMENT ==========
  const getImages = useCallback(
    (jobId) => dispatch(fetchJobImages(jobId)),
    [dispatch]
  );

  const uploadRefImages = useCallback(
    (jobId, images) => dispatch(uploadReferenceImages({ jobId, images })),
    [dispatch]
  );

  const deleteRefImage = useCallback(
    (jobId, imageUrl) => dispatch(deleteReferenceImage({ jobId, imageUrl })),
    [dispatch]
  );

  const uploadCompImages = useCallback(
    (jobId, images) => dispatch(uploadCompletedImages({ jobId, images })),
    [dispatch]
  );

  const deleteCompImage = useCallback(
    (jobId, imageUrl) => dispatch(deleteCompletedImage({ jobId, imageUrl })),
    [dispatch]
  );

  const uploadItmImages = useCallback(
    (jobId, images) => dispatch(uploadItemImages({ jobId, images })),
    [dispatch]
  );

  const uploadQcImages = useCallback(
    (jobId, images) => dispatch(uploadQCImages({ jobId, images })),
    [dispatch]
  );

  const bulkDeleteImgs = useCallback(
    (jobId, imageUrls, imageType) =>
      dispatch(bulkDeleteImages({ jobId, imageUrls, imageType })),
    [dispatch]
  );

  const uploadDesign = useCallback(
    (jobId, file) => dispatch(uploadDesignFile({ jobId, file })),
    [dispatch]
  );

  // ========== MATERIAL MANAGEMENT - EXISTING (with image support) ==========
  const getMaterials = useCallback(
    (jobId) => dispatch(fetchJobMaterials(jobId)),
    [dispatch]
  );

  const issueMat = useCallback(
    (jobId, materialData, images = []) =>
      dispatch(issueMaterial({ jobId, materialData, images })),
    [dispatch]
  );

  const confirmReceived = useCallback(
    (jobId, materialId, notes) =>
      dispatch(confirmMaterialReceived({ jobId, materialId, notes })),
    [dispatch]
  );

  const recordReturn = useCallback(
    (jobId, returnData, images = []) =>
      dispatch(recordMaterialReturn({ jobId, returnData, images })),
    [dispatch]
  );

  // ========== MATERIAL MANAGEMENT - CRUD OPERATIONS ==========

  // Update issued material
  const updateMaterial = useCallback(
    (jobId, materialId, materialData, forceUpdate = false) =>
      dispatch(
        updateIssuedMaterial({ jobId, materialId, materialData, forceUpdate })
      ),
    [dispatch]
  );

  // Delete issued material
  const deleteMaterial = useCallback(
    (jobId, materialId, reason, forceDelete = false) =>
      dispatch(
        deleteIssuedMaterial({ jobId, materialId, reason, forceDelete })
      ),
    [dispatch]
  );

  // Undo material received confirmation
  const undoReceived = useCallback(
    (jobId, materialId, reason) =>
      dispatch(undoMaterialReceived({ jobId, materialId, reason })),
    [dispatch]
  );

  // Update/adjust material return record
  const updateReturn = useCallback(
    (jobId, returnData, adjustmentReason, images = []) =>
      dispatch(
        updateMaterialReturn({ jobId, returnData, adjustmentReason, images })
      ),
    [dispatch]
  );

  // Delete/reset material return record
  const deleteReturn = useCallback(
    (jobId, reason) => dispatch(deleteMaterialReturn({ jobId, reason })),
    [dispatch]
  );

  // Set material permissions manually
  const setMatPermissions = useCallback(
    (permissions) => dispatch(setMaterialPermissions(permissions)),
    [dispatch]
  );

  // ========== QUALITY CHECK WORKFLOW (with image support) ==========
  const submitQC = useCallback(
    (jobId, notes, images = [], finishedWeight) =>
      dispatch(submitForQualityCheck({ jobId, notes, images, finishedWeight })),
    [dispatch]
  );

  const qualityCheck = useCallback(
    (params) => dispatch(performQualityCheck(params)),
    [dispatch]
  );

  const passQC = useCallback(
    (
      jobId,
      rating,
      score,
      notes,
      finalWeight,
      finalPurity,
      hallmarkNumber,
      images = []
    ) =>
      dispatch(
        performQualityCheck({
          jobId,
          passed: true,
          rating,
          score,
          notes,
          finalWeight,
          finalPurity,
          hallmarkNumber,
          images,
        })
      ),
    [dispatch]
  );

  const failQC = useCallback(
    (jobId, revisionReason, notes, images = []) =>
      dispatch(
        performQualityCheck({
          jobId,
          passed: false,
          revisionReason,
          notes,
          images,
        })
      ),
    [dispatch]
  );

  const completeRev = useCallback(
    (jobId, notes, images = []) =>
      dispatch(completeRevision({ jobId, notes, images })),
    [dispatch]
  );

  const markDelivered = useCallback(
    (jobId, notes, deliveredTo) =>
      dispatch(markJobDelivered({ jobId, notes, deliveredTo })),
    [dispatch]
  );

  // ========== JOB PAYMENT ==========
  const getPayments = useCallback(
    (jobId) => dispatch(fetchJobPayments(jobId)),
    [dispatch]
  );

  const setPricing = useCallback(
    (jobId, pricingData) => dispatch(updateJobPricing({ jobId, pricingData })),
    [dispatch]
  );

  const recordPayment = useCallback(
    (jobId, paymentData) => dispatch(recordJobPayment({ jobId, paymentData })),
    [dispatch]
  );

  const addDeduction = useCallback(
    (jobId, deductionData) =>
      dispatch(addJobDeduction({ jobId, deductionData })),
    [dispatch]
  );

  // NEW: Remove deduction
  const removeDeduction = useCallback(
    (jobId, deductionIndex) =>
      dispatch(removeJobDeduction({ jobId, deductionIndex })),
    [dispatch]
  );

  // ========== PRICING PREVIEW ==========

  // Get pricing calculation preview
  const getPricingPreview = useCallback(
    (jobId) => dispatch(fetchPricingPreview(jobId)),
    [dispatch]
  );

  // Clear pricing preview
  const clearPricingPreviewFn = useCallback(
    () => dispatch(clearPricingPreview()),
    [dispatch]
  );

  // ========== PROGRESS ==========
  const addProgress = useCallback(
    (jobId, progressPercentage, notes, images) =>
      dispatch(addJobProgress({ jobId, progressPercentage, notes, images })),
    [dispatch]
  );

  // ========== MESSAGING (with attachment support) ==========
  const sendMessage = useCallback(
    (jobId, message, attachments = []) =>
      dispatch(sendJobMessage({ jobId, message, attachments })),
    [dispatch]
  );

  // ========== ORDER-KARIGAR INTEGRATION ==========
  const assignToOrderItem = useCallback(
    (orderId, orderItemId, karigarId, expectedCompletionDate, notes) =>
      dispatch(
        assignKarigarToOrderItem({
          orderId,
          orderItemId,
          karigarId,
          expectedCompletionDate,
          notes,
        })
      ),
    [dispatch]
  );

  const reassignForOrderItem = useCallback(
    (orderId, orderItemId, jobId, newKarigarId, reason) =>
      dispatch(
        reassignKarigarForOrderItem({
          orderId,
          orderItemId,
          jobId,
          newKarigarId,
          reason,
        })
      ),
    [dispatch]
  );

  const bulkAssign = useCallback(
    (assignments) => dispatch(bulkAssignKarigars(assignments)),
    [dispatch]
  );

  const getOrderStatus = useCallback(
    (orderId) => dispatch(getOrderKarigarStatus(orderId)),
    [dispatch]
  );

  const getQueue = useCallback(
    (karigarId) => dispatch(getKarigarQueue(karigarId)),
    [dispatch]
  );

  const getOrderWorkStatus = useCallback(
    (orderId) => dispatch(getOrderKarigarWorkStatus(orderId)),
    [dispatch]
  );

  const assignViaOrder = useCallback(
    (orderId, itemIndex, karigarId, workDetails) =>
      dispatch(
        assignKarigarViaOrder({ orderId, itemIndex, karigarId, workDetails })
      ),
    [dispatch]
  );

  const checkCanFulfill = useCallback(
    (orderId) => dispatch(checkOrderCanFulfill(orderId)),
    [dispatch]
  );

  // ========== FILTERS & STATE ==========
  const updateFilters = useCallback(
    (newFilters) => dispatch(setJobFilters(newFilters)),
    [dispatch]
  );

  const clearFilters = useCallback(
    () => dispatch(resetJobFilters()),
    [dispatch]
  );

  const clearJob = useCallback(() => dispatch(clearCurrentJob()), [dispatch]);

  const clearKarigars = useCallback(
    () => dispatch(clearAvailableKarigars()),
    [dispatch]
  );

  const clearTimelineFn = useCallback(
    () => dispatch(clearTimeline()),
    [dispatch]
  );

  const clearOrderStatusFn = useCallback(
    () => dispatch(clearOrderStatus()),
    [dispatch]
  );

  const clearQueueFn = useCallback(
    () => dispatch(clearKarigarQueue()),
    [dispatch]
  );

  const clearMaterialsFn = useCallback(
    () => dispatch(clearJobMaterials()),
    [dispatch]
  );

  const clearPaymentsFn = useCallback(
    () => dispatch(clearJobPayments()),
    [dispatch]
  );

  const clearImagesFn = useCallback(() => {
    if (typeof clearJobImages === "function") {
      dispatch(clearJobImages());
    }
  }, [dispatch]);

  const clearCanFulfillFn = useCallback(
    () => dispatch(clearCanFulfillCheck()),
    [dispatch]
  );

  const clearWorkStatusFn = useCallback(
    () => dispatch(clearOrderKarigarWorkStatus()),
    [dispatch]
  );

  const clearErr = useCallback(() => dispatch(clearJobError()), [dispatch]);

  const clearSuccess = useCallback(
    () => dispatch(clearJobSuccessMessage()),
    [dispatch]
  );

  return {
    // State - EXISTING
    jobs,
    currentJob,
    orderDetails,
    productDetails,
    pagination,
    filters,
    loading,
    error,
    successMessage,
    availableKarigars,
    productKarigars,
    otherKarigars,
    allKarigars,
    recommendations,
    availableKarigarsSummary,
    stats,
    timeline,
    orderStatus,
    karigarQueue,
    jobTypes,
    currentJobMaterials,
    currentJobPayments,
    currentJobImages,
    orderKarigarWorkStatus,
    canFulfillCheck,
    allowedTransitions,
    canBeModified,

    // Material permissions state
    materialPermissions,
    canIssueMaterial,
    canRecordReturn,
    canEditMaterials,

    // Material detail state
    issuedMaterials,
    returnedMaterials,
    materialReturnRecord,
    materialSummary,
    wastage,
    hasMaterialsReturned,
    receivedMaterialsCount,
    pendingReceiptMaterials,

    // Material loading state
    isMaterialUpdating,
    isMaterialDeleting,
    isMaterialReturning,
    isAnyMaterialLoading,

    // Pricing Preview state
    pricingPreview,
    pricingPreviewLoading,
    canCalculatePricing,
    missingPricingFields,
    allRateTypePreviews,

    // NEW: Job Payment state
    jobPricing,
    karigarPaymentInfo,
    completedJobPayments,
    pendingJobPayments,
    jobDeductions,
    karigarTotalPayable,
    karigarTotalPaid,
    karigarPendingAmount,
    karigarPaymentStatus,
    isJobFullyPaid,
    isJobPartiallyPaid,
    jobHasPayments,
    jobHasPendingPayments,
    jobPaymentSummary,

    // NEW: Payment loading state
    isRecordingPayment,
    isUpdatingPricing,
    isAddingDeduction,
    isRemovingDeduction,
    isFetchingJobPayments,
    isAnyPaymentOperationLoading,

    // List & Search
    getJobs,
    getJobById,
    getStats,
    getJobTypes,
    getJobsByKarigar,
    getJobsByOrder,
    getTimeline,

    // Job Creation (with image support)
    addJob,
    addRepairJob,
    addAlterationJob,
    addInternalJob,
    receiveItem,

    // Job Editing & Status
    editJob,
    changeStatus,

    // Assignment
    assign,
    reassign,
    getAvailableKarigars,

    // Cancel
    cancel,
    cancelMultiple,

    // Image Management
    getImages,
    uploadReferenceImages: uploadRefImages,
    deleteReferenceImage: deleteRefImage,
    uploadCompletedImages: uploadCompImages,
    deleteCompletedImage: deleteCompImage,
    uploadItemImages: uploadItmImages,
    uploadQCImages: uploadQcImages,
    bulkDeleteImages: bulkDeleteImgs,
    uploadDesignFile: uploadDesign,

    // Material Management - EXISTING (with image support)
    getMaterials,
    issueMaterial: issueMat,
    confirmMaterialReceived: confirmReceived,
    recordMaterialReturn: recordReturn,

    // Material Management - CRUD Operations
    updateIssuedMaterial: updateMaterial,
    deleteIssuedMaterial: deleteMaterial,
    undoMaterialReceived: undoReceived,
    updateMaterialReturn: updateReturn,
    deleteMaterialReturn: deleteReturn,
    setMaterialPermissions: setMatPermissions,

    // Quality Check Workflow (with image support)
    submitForQualityCheck: submitQC,
    performQualityCheck: qualityCheck,
    passQualityCheck: passQC,
    failQualityCheck: failQC,
    completeRevision: completeRev,
    markDelivered,

    // Job Payment
    getJobPayments: getPayments,
    setPricing,
    recordPayment,
    addDeduction,
    removeDeduction, // NEW

    // Pricing Preview methods
    getPricingPreview,
    clearPricingPreview: clearPricingPreviewFn,

    // Progress
    addProgress,

    // Messaging (with attachment support)
    sendMessage,

    // Order-Karigar Integration
    assignToOrderItem,
    reassignForOrderItem,
    bulkAssign,
    getOrderStatus,
    getKarigarQueue: getQueue,
    getOrderWorkStatus,
    assignViaOrder,
    checkCanFulfill,

    // Clear Actions
    updateFilters,
    clearFilters,
    clearCurrentJob: clearJob,
    clearAvailableKarigars: clearKarigars,
    clearTimeline: clearTimelineFn,
    clearOrderStatus: clearOrderStatusFn,
    clearKarigarQueue: clearQueueFn,
    clearJobMaterials: clearMaterialsFn,
    clearJobPayments: clearPaymentsFn,
    clearJobImages: clearImagesFn,
    clearCanFulfillCheck: clearCanFulfillFn,
    clearOrderKarigarWorkStatus: clearWorkStatusFn,
    clearError: clearErr,
    clearSuccessMessage: clearSuccess,
  };
};

// ============================================================================
// HOOK: usePaymentManagement
// ============================================================================

export const usePaymentManagement = () => {
  const dispatch = useDispatch();

  // Selectors
  const payments = useSelector(selectors.selectPayments);
  const currentPayment = useSelector(selectors.selectCurrentPayment);
  const pagination = useSelector(selectors.selectPaymentPagination);
  const filters = useSelector(selectors.selectPaymentFilters);
  const loading = useSelector(selectors.selectPaymentLoading);
  const error = useSelector(selectors.selectPaymentError);
  const successMessage = useSelector(selectors.selectPaymentSuccessMessage);
  const pendingPayments = useSelector(selectors.selectPendingPayments);
  const pendingCount = useSelector(selectors.selectPendingPaymentCount);
  const pendingTotal = useSelector(selectors.selectPendingPaymentTotal);
  const stats = useSelector(selectors.selectPaymentStats);
  const report = useSelector(selectors.selectPaymentReport);
  const earnings = useSelector(selectors.selectKarigarEarnings);
  const jobPaymentCalc = useSelector(selectors.selectJobPaymentCalculation);

  // NEW: Payment action loading selectors
  const isCreatingPayment = useSelector(selectors.selectIsCreatingPayment);
  const isProcessingPayment = useSelector(selectors.selectIsProcessingPayment);
  const isCancellingPayment = useSelector(selectors.selectIsCancellingPayment);
  const isApprovingPayment = useSelector(selectors.selectIsApprovingPayment);
  const isRejectingPayment = useSelector(selectors.selectIsRejectingPayment);
  const isHoldingPayment = useSelector(selectors.selectIsHoldingPayment);
  const isReleasingPayment = useSelector(selectors.selectIsReleasingPayment);
  const isBulkProcessing = useSelector(selectors.selectIsBulkProcessing);
  const isAnyPaymentActionLoading = useSelector(
    selectors.selectIsAnyPaymentActionLoading
  );

  // ========== LIST & SEARCH ==========
  const getPayments = useCallback(
    (params) => dispatch(fetchPayments(params)),
    [dispatch]
  );

  const getPaymentById = useCallback(
    (id) => dispatch(fetchPaymentById(id)),
    [dispatch]
  );

  const getPaymentsByKarigar = useCallback(
    (karigarId, params) =>
      dispatch(fetchPaymentsByKarigar({ karigarId, params })),
    [dispatch]
  );

  const getPaymentsByJob = useCallback(
    (jobId) => dispatch(fetchPaymentsByJob(jobId)),
    [dispatch]
  );

  const getPending = useCallback(
    (params) => dispatch(fetchPendingPayments(params)),
    [dispatch]
  );

  const getStats = useCallback(
    (params) => dispatch(fetchPaymentStats(params)),
    [dispatch]
  );

  // ========== CRUD ==========
  const addPayment = useCallback(
    (data) => dispatch(createPayment(data)),
    [dispatch]
  );

  const editPayment = useCallback(
    (paymentId, data) => dispatch(updatePayment({ paymentId, data })),
    [dispatch]
  );

  // ========== PAYMENT ACTIONS ==========
  const approve = useCallback(
    (paymentId, notes) => dispatch(approvePayment({ paymentId, notes })),
    [dispatch]
  );

  const reject = useCallback(
    (paymentId, reason) => dispatch(rejectPayment({ paymentId, reason })),
    [dispatch]
  );

  const process = useCallback(
    (paymentId, paymentMethod, transactionId, referenceNumber, notes) =>
      dispatch(
        processPayment({
          paymentId,
          paymentMethod,
          transactionId,
          referenceNumber,
          notes,
        })
      ),
    [dispatch]
  );

  const hold = useCallback(
    (paymentId, reason) => dispatch(holdPayment({ paymentId, reason })),
    [dispatch]
  );

  const release = useCallback(
    (paymentId) => dispatch(releasePayment(paymentId)),
    [dispatch]
  );

  // NEW: Cancel payment
  const cancel = useCallback(
    (paymentId, reason) => dispatch(cancelPayment({ paymentId, reason })),
    [dispatch]
  );

  // ========== SPECIAL PAYMENT TYPES ==========
  const createAdvance = useCallback(
    (karigarId, amount, notes, branchCode, branchName) =>
      dispatch(
        createAdvancePayment({
          karigarId,
          amount,
          notes,
          branchCode,
          branchName,
        })
      ),
    [dispatch]
  );

  const createDed = useCallback(
    (karigarId, amount, type, description, notes, branchCode) =>
      dispatch(
        createDeduction({
          karigarId,
          amount,
          type,
          description,
          notes,
          branchCode,
        })
      ),
    [dispatch]
  );

  // ========== CALCULATION ==========
  const calculateJob = useCallback(
    (jobId) => dispatch(calculateJobPayment(jobId)),
    [dispatch]
  );

  // ========== BULK OPERATIONS ==========
  const generateForPeriod = useCallback(
    (periodStart, periodEnd, branchCode) =>
      dispatch(
        generatePaymentsForPeriod({ periodStart, periodEnd, branchCode })
      ),
    [dispatch]
  );

  const bulkProcess = useCallback(
    (paymentIds, paymentMethod, notes) =>
      dispatch(bulkProcessPayments({ paymentIds, paymentMethod, notes })),
    [dispatch]
  );

  // ========== REPORTS ==========
  const generateReport = useCallback(
    (reportParams) => dispatch(generatePaymentReport(reportParams)),
    [dispatch]
  );

  const getEarnings = useCallback(
    (karigarId, params) =>
      dispatch(fetchKarigarEarnings({ karigarId, params })),
    [dispatch]
  );

  const exportPay = useCallback(
    (exportParams) => dispatch(exportPayments(exportParams)),
    [dispatch]
  );

  // ========== FILTERS & STATE ==========
  const updateFilters = useCallback(
    (newFilters) => dispatch(setPaymentFilters(newFilters)),
    [dispatch]
  );

  const clearFilters = useCallback(
    () => dispatch(resetPaymentFilters()),
    [dispatch]
  );

  const setPagination = useCallback(
    (paginationData) => dispatch(setPaymentPagination(paginationData)),
    [dispatch]
  );

  const clearPayment = useCallback(
    () => dispatch(clearCurrentPayment()),
    [dispatch]
  );

  const clearRep = useCallback(() => dispatch(clearReport()), [dispatch]);

  const clearEarn = useCallback(() => dispatch(clearEarnings()), [dispatch]);

  const clearCalc = useCallback(
    () => dispatch(clearJobPaymentCalculation()),
    [dispatch]
  );

  // NEW: Clear pending payments
  const clearPending = useCallback(
    () => dispatch(clearPendingPayments()),
    [dispatch]
  );

  // NEW: Update payment in list (optimistic)
  const updateInList = useCallback(
    (payment) => dispatch(updatePaymentInList(payment)),
    [dispatch]
  );

  // NEW: Remove from pending list
  const removeFromPendingList = useCallback(
    (paymentId) => dispatch(removeFromPending(paymentId)),
    [dispatch]
  );

  const clearErr = useCallback(() => dispatch(clearPaymentError()), [dispatch]);

  const clearSuccess = useCallback(
    () => dispatch(clearPaymentSuccessMessage()),
    [dispatch]
  );

  return {
    // State
    payments,
    currentPayment,
    pagination,
    filters,
    loading,
    error,
    successMessage,
    pendingPayments,
    pendingCount,
    pendingTotal,
    stats,
    report,
    earnings,
    jobPaymentCalculation: jobPaymentCalc,

    // NEW: Payment action loading state
    isCreatingPayment,
    isProcessingPayment,
    isCancellingPayment,
    isApprovingPayment,
    isRejectingPayment,
    isHoldingPayment,
    isReleasingPayment,
    isBulkProcessing,
    isAnyPaymentActionLoading,

    // List & Search
    getPayments,
    getPaymentById,
    getPaymentsByKarigar,
    getPaymentsByJob,
    getPendingPayments: getPending,
    getStats,

    // CRUD
    addPayment,
    editPayment,

    // Payment Actions
    approve,
    reject,
    processPayment: process,
    hold,
    release,
    cancelPayment: cancel, // NEW

    // Special Payment Types
    createAdvancePayment: createAdvance,
    createDeduction: createDed,

    // Calculation
    calculateJobPayment: calculateJob,

    // Bulk Operations
    generateForPeriod,
    bulkProcess,

    // Reports
    generateReport,
    getEarnings,
    exportPayments: exportPay,

    // Filters & State
    updateFilters,
    clearFilters,
    setPagination,
    clearPayment,
    clearReport: clearRep,
    clearEarnings: clearEarn,
    clearJobPaymentCalculation: clearCalc,
    clearPendingPayments: clearPending, // NEW
    updatePaymentInList: updateInList, // NEW
    removeFromPending: removeFromPendingList, // NEW
    clearError: clearErr,
    clearSuccessMessage: clearSuccess,
  };
};

// ============================================================================
// COMBINED HOOK
// ============================================================================

export const useKarigar = () => {
  const karigar = useKarigarManagement();
  const job = useJobManagement();
  const payment = usePaymentManagement();

  return {
    karigar,
    job,
    payment,
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default useKarigar;
